import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { MULTI_VIEW_PRESETS } from '../constants';

const HEADER_PX = 56; // 3.5rem — matches .lp-header
const BANNER_PX = 40; // 2.5rem — matches .lp-shell--with-banner
const MD_BREAKPOINT = 768;

const CUTOFF_COLORS = {
  iphone: '#38bdf8',
  desktop: '#fbbf24',
  wide: '#f472b6',
};

/**
 * Hero box the background image covers (device chrome + white border removed).
 * Matches landingPreviewCss layout.
 */
function getHeroSize(preset, showAnnouncementBanner) {
  const border = preset.width >= MD_BREAKPOINT ? 16 : 8;
  const chrome = HEADER_PX + (showAnnouncementBanner ? BANNER_PX : 0);
  return {
    width: Math.max(preset.width - border * 2, 1),
    height: Math.max(preset.height - chrome - border * 2, 1),
  };
}

/**
 * Visible rect in natural image pixels for object-fit: cover + object-position: center.
 */
function getCoverVisibleRect(imageWidth, imageHeight, boxWidth, boxHeight) {
  const scale = Math.max(boxWidth / imageWidth, boxHeight / imageHeight);
  const width = boxWidth / scale;
  const height = boxHeight / scale;
  return {
    x: (imageWidth - width) / 2,
    y: (imageHeight - height) / 2,
    width,
    height,
  };
}

function BackgroundCutoffPreview({ imageUrl, showAnnouncementBanner }) {
  const [naturalSize, setNaturalSize] = useState(null);

  useEffect(() => {
    if (!imageUrl) {
      setNaturalSize(null);
      return undefined;
    }

    let cancelled = false;
    const image = new Image();
    image.onload = () => {
      if (!cancelled) {
        setNaturalSize({
          width: image.naturalWidth,
          height: image.naturalHeight,
        });
      }
    };
    image.onerror = () => {
      if (!cancelled) setNaturalSize(null);
    };
    image.src = imageUrl;

    return () => {
      cancelled = true;
    };
  }, [imageUrl]);

  const frames = useMemo(() => {
    if (!naturalSize) return [];

    return MULTI_VIEW_PRESETS.map((preset) => {
      const hero = getHeroSize(preset, showAnnouncementBanner);
      const visible = getCoverVisibleRect(
        naturalSize.width,
        naturalSize.height,
        hero.width,
        hero.height,
      );
      return {
        id: preset.id,
        label: preset.label,
        color: CUTOFF_COLORS[preset.id] || '#fff',
        leftPct: (visible.x / naturalSize.width) * 100,
        topPct: (visible.y / naturalSize.height) * 100,
        widthPct: (visible.width / naturalSize.width) * 100,
        heightPct: (visible.height / naturalSize.height) * 100,
      };
    });
  }, [naturalSize, showAnnouncementBanner]);

  if (!imageUrl) return null;

  return (
    <div className="landing-page-simulator__cutoff">
      <div className="landing-page-simulator__cutoff-stage">
        <img
          className="landing-page-simulator__cutoff-img"
          src={imageUrl}
          alt=""
        />
        {frames.map((frame) => (
          <div
            key={frame.id}
            className="landing-page-simulator__cutoff-frame"
            style={{
              left: `${frame.leftPct}%`,
              top: `${frame.topPct}%`,
              width: `${frame.widthPct}%`,
              height: `${frame.heightPct}%`,
              borderColor: frame.color,
            }}
            title={`${frame.label} visible area`}
          />
        ))}
      </div>
      <div className="landing-page-simulator__cutoff-legend">
        {frames.map((frame) => (
          <span key={frame.id} className="landing-page-simulator__cutoff-swatch">
            <span
              className="landing-page-simulator__cutoff-dot"
              style={{ background: frame.color }}
            />
            {frame.label}
          </span>
        ))}
      </div>
    </div>
  );
}

BackgroundCutoffPreview.propTypes = {
  imageUrl: PropTypes.string,
  showAnnouncementBanner: PropTypes.bool,
};

BackgroundCutoffPreview.defaultProps = {
  imageUrl: undefined,
  showAnnouncementBanner: true,
};

export default BackgroundCutoffPreview;
